/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: inherit
 */
import React, { useCallback, useEffect, useState } from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import { TicketAlertStrip } from '../../shared/ticket/TicketAlertStrip'
import { ticketColors, ticketFonts } from '../../shared/theme/ticketColors';
import {
  getMergedActiveCashSessionId,
  subscribeCashSessionChangeSignalDebounced,
} from '../../cash/infra-api';
import { navigateInShell } from '../../shell/navigation/rootNavigation';
import { useSessionStore } from '../../shell/session/store/useSessionStore';

type Props = {
  /** Copy corto tipo POS pill, o banner con explicación. */
  variant: 'inbox' | 'detail';
  /** Si true, hay canal online relevante (tienda publicada o pedidos abiertos). */
  onlineRelevant: boolean;
  /** Callback cuando cambia si hay sesión (para gate de cobro). */
  onActiveSessionChange?: (sessionId: string | null) => void;
};

/**
 * Alerta temprana: caja cerrada en Pedidos. No bloquea prep; CTA a Caja.
 */
export function OrdersCashClosedBanner({
  variant,
  onlineRelevant,
  onActiveSessionChange,
}: Props) {
  const navigation = useNavigation();
  const branchId = useSessionStore((s) => s.currentBranchId);
  const [sessionId, setSessionId] = useState<string | null | undefined>(undefined);

  const refresh = useCallback(async () => {
    if (!branchId) {
      setSessionId(null);
      onActiveSessionChange?.(null);
      return;
    }
    try {
      const id = await getMergedActiveCashSessionId(branchId);
      setSessionId(id);
      onActiveSessionChange?.(id);
    } catch {
      setSessionId(null);
      onActiveSessionChange?.(null);
    }
  }, [branchId, onActiveSessionChange]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    return subscribeCashSessionChangeSignalDebounced(() => {
      void refresh();
    });
  }, [refresh]);

  const cashClosed = sessionId === null;
  const show = onlineRelevant && cashClosed;
  if (!show) return null;

  const openCash = () => {
    navigateInShell(navigation, 'CashManagement');
  };

  if (variant === 'inbox') {
    return (
      <TicketAlertStrip
        title="Caja cerrada"
        subtitle="Los pedidos entran igual. Abrí caja antes de cobrar los que pagan al retirar."
        tone="warn"
        onPress={openCash}
        testID="orders.alert.cash-closed"
        accessibilityLabel="Abrir caja"
      />
    );
  }

  return (
    <View style={styles.detailBox}>
      <View style={styles.detailTitleRow}>
        <Ionicons name="alert-circle-outline" size={16} color="#B45309" />
        <Text style={styles.detailTitle}>Caja cerrada</Text>
      </View>
      <Text style={styles.detailText}>
        Este se cobra en efectivo. Abrí caja para anotarlo.
      </Text>
      <TouchableOpacity onPress={openCash} accessibilityRole="button" style={styles.detailCta}>
        <Text style={styles.detailCtaText}>Abrir caja</Text>
      </TouchableOpacity>
    </View>
  );
}

const styles = StyleSheet.create({
  detailBox: {
    gap: 8,
    padding: 12,
    borderRadius: 10,
    backgroundColor: ticketColors.warnBg,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: 'rgba(180, 83, 9, 0.4)',
  },
  detailTitleRow: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  detailTitle: {
    fontSize: 13,
    fontWeight: '700',
    color: '#B45309',
    fontFamily: ticketFonts.sans,
  },
  detailText: {
    fontSize: 13,
    lineHeight: 18,
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
  },
  detailCta: {
    alignSelf: 'flex-start',
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 8,
    backgroundColor: ticketColors.gold,
  },
  detailCtaText: {
    fontSize: 13,
    fontWeight: '700',
    color: ticketColors.goldInk,
    fontFamily: ticketFonts.sans,
  },
});
